import React, { useCallback, useEffect, useMemo, useState } from "react";
import { View, StatusBar, SafeAreaView } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import Icons from "@/assets/icons";
import { ActionButton } from "@/components/ActionButton";
import Animated, { FadeInRight, SlideOutLeft } from "react-native-reanimated";
import OnboardSlide1 from "../../components/onboard/Slide1";
import OnboardSlide2 from "../../components/onboard/Slide2";
import OnboardSlide3 from "../../components/onboard/Slide3";
import OnboardSlide4 from "../../components/onboard/Slide4";
import OnboardSlide5 from "../../components/onboard/Slide5";
import OnboardSlide6 from "../../components/onboard/Slide6";
import OnboardSlide7 from "../../components/onboard/Slide7";
import OnboardSlide8 from "../../components/onboard/Slide8";
import OnboardSlide9 from "../../components/onboard/Slide9";
import OnboardSlide10 from "../../components/onboard/Slide10";
import OnboardSlide11 from "../../components/onboard/Slide11";
import OnboardSlide12 from "../../components/onboard/Slide12";
import OnboardSlide13 from "../../components/onboard/Slide13";
import OnboardSlide14 from "../../components/onboard/Slide14";
import OnboardSlide15 from "../../components/onboard/Slide15";
import OnboardSlide16 from "../../components/onboard/Slide16";
import Constants from "expo-constants";
import FBTrackingService from "@/services/fbTrackingService";
import { TRACKING_EVENTS } from "@/constants/trackingEvents";
import { useLocalSearchParams, router } from "expo-router";

const SLIDE_COUNT = 16;
const PROGRESS_SLIDE_COUNT = 9; // Only count slides 1-9 for progress bar (slide 16 is sign up)

export default function OnboardScreen() {
  const { startAt } = useLocalSearchParams<{ startAt?: string }>();
  const [index, setIndex] = useState(() => {
    const start = startAt ? parseInt(startAt, 10) : 0;
    return isNaN(start) ? 0 : Math.min(Math.max(start, 0), SLIDE_COUNT - 1);
  });

  // Track onboarding started on mount
  useEffect(() => {
    FBTrackingService.trackEvent(TRACKING_EVENTS.ONBOARDING_STARTED, {
      eventData: {
        total_steps: SLIDE_COUNT,
      },
    });
  }, []);

  const next = useCallback(() => {
    // Track step completion before moving to next
    FBTrackingService.trackEvent(TRACKING_EVENTS.ONBOARDING_STEP_COMPLETED, {
      eventData: {
        step_number: index + 1,
        total_steps: SLIDE_COUNT,
        progress_percentage: ((index + 1) / SLIDE_COUNT) * 100,
      },
    });

    setIndex((prev) => prev + 1);
  }, [index]);

  const goToSignUp = useCallback(() => {
    // Track step completion before jumping to sign up
    FBTrackingService.trackEvent(TRACKING_EVENTS.ONBOARDING_STEP_COMPLETED, {
      eventData: {
        step_number: index + 1,
        total_steps: SLIDE_COUNT,
        progress_percentage: ((index + 1) / SLIDE_COUNT) * 100,
      },
    });

    // Jump to Slide16 (index 15) - the sign up form
    setIndex(15);
  }, [index]);

  const goToRateUs = useCallback(() => {
    router.push("/signup/rate-us");
  }, []);

  const slides = useMemo(
    () => [
      {
        component: <OnboardSlide1 key={0} />,
      },
      {
        component: <OnboardSlide2 key={1} />,
      },
      {
        component: <OnboardSlide3 key={2} />,
      },
      {
        component: <OnboardSlide4 key={3} />,
      },
      {
        component: <OnboardSlide5 key={4} />,
      },
      {
        component: <OnboardSlide6 key={5} />,
      },
      {
        component: <OnboardSlide7 key={6} onComplete={next} />,
        button: {
          hide: true,
        },
      },
      {
        component: <OnboardSlide8 key={7} />,
        button: {
          title: "Claim now",
          onPress: goToRateUs,
        },
      },
      {
        component: <OnboardSlide9 key={8} />,
        button: {
          onPress: goToSignUp,
        },
      },
      {
        component: <OnboardSlide10 key={9} />,
        hideLogo: true,
      },
      {
        component: <OnboardSlide11 key={10} />,
        hideLogo: true,
      },
      {
        component: <OnboardSlide12 key={11} />,
        hideLogo: true,
      },
      {
        component: <OnboardSlide13 key={12} next={next} />,
        button: {
          hide: true,
        },
      },
      {
        component: <OnboardSlide14 key={13} />,
        button: {
          title: "Claim now",
        },
      },
      {
        component: <OnboardSlide15 key={14} />,
        button: {
          title: "Unlock Free",
        },
      },
      {
        component: <OnboardSlide16 key={15} />,
        button: {
          hide: true,
        },
        hideHeader: true,
      },
    ],
    [next, goToSignUp, goToRateUs]
  );

  // Progress bar only counts first 9 slides, max out at 100% for slide 9+
  const progress = Math.min((index + 1) / PROGRESS_SLIDE_COUNT, 1);

  return (
    <View style={styles.container}>
      <StatusBar barStyle="dark-content" />
      {/* Header with progress and logo */}
      {!slides[index].hideHeader && (
        <View style={styles.header}>
          <View style={styles.progressBarBg}>
            <View
              style={[styles.progressBarFill, { width: `${progress * 100}%` }]}
            />
          </View>
          <Icons.LogoWithTextBlack width={170} height={38} />
        </View>
      )}

      {/* Slides container */}
      <View style={styles.slideContainer}>
        <Animated.View
          key={`slide-${index}`}
          entering={FadeInRight.duration(300)}
          exiting={SlideOutLeft.duration(300)}
          style={styles.slide}
        >
          {slides[index].component}
        </Animated.View>
      </View>

      {/* Bottom button */}
      {!slides[index].button?.hide && (
        <View style={styles.bottomAbsolute} pointerEvents="box-none">
          <ActionButton
            title={slides[index].button?.title || "Next"}
            onPress={slides[index].button?.onPress || next}
            style={styles.nextBtn}
          />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: Constants.statusBarHeight,
    backgroundColor: "#FFFFFF",
  },
  header: {
    paddingHorizontal: 16,
    paddingTop: 24,
    gap: 24,
    alignItems: "center",
  },
  progressBarBg: {
    width: "100%",
    height: 6,
    backgroundColor: "#E5E5E5",
    borderRadius: 100,
    overflow: "hidden",
  },
  progressBarFill: {
    height: 6,
    backgroundColor: "#0F9129",
    borderRadius: 100,
  },
  slideContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 24,
    paddingBottom: 100,
  },
  slide: {
    flex: 1,
    width: "100%",
    justifyContent: "center",
  },
  bottomAbsolute: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 36,
    paddingHorizontal: 24,
  },
  nextBtn: {
    width: "100%",
  },
});
